// Import necessary functions and variables

import { math, mix, parseToRgb } from 'polished';

// Bootstrap functions
//
// Utility mixins and functions for evaluating source code across our variables, maps, and mixins.

// Ascending
// Used to evaluate Sass maps like our grid breakpoints.
const sizes = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
export function assertAscending(map, mapName) {
  let prevKey = null;
  let prevNum = null;
  const orderedKeys = [...sizes].filter(size => map[size] != null);
  orderedKeys.forEach(key => {
    let num = map[key];
    if (prevNum === null || unit(num) == '%' || unit(prevNum) == '%') {
      // Do nothing
    } else if (!comparable(prevNum, num) && prevNum !== '0') {
      console.warn(
        `Potentially invalid value for ${mapName}: This map must be in ascending order, but key '${key}' has value ${num} whose unit makes it incomparable to ${prevNum}, the value of the previous key '${prevKey}' !`
      );
    } else {
      let _num = splitUnit(num);
      let _prevNumber = splitUnit(prevNum);
      if (_prevNumber[0] >= _num[0]) {
        console.warn(
          `Invalid value for ${mapName}: This map must be in ascending order, but key '${key}' has value ${num} which isn't greater than ${prevNum}, the value of the previous key '${prevKey}' !`
        );
      }
    }
    prevKey = key;
    prevNum = num;
  });
}

// Starts at zero
// Used to ensure the min-width of the lowest breakpoint starts at 0.
export function assertStartsAtZero(map, mapName = '$grid-breakpoints') {
  const orderedKeys = [...sizes].filter(size => map[size] != null);
  if (orderedKeys.length > 0) {
    const firstValue = map[orderedKeys[0]];
    if (![0, '0', '0px', '0%', '0rem', '0em'].includes(firstValue)) {
      console.warn(`First breakpoint in ${mapName} must start at 0, but starts at ${firstValue}.`);
    }
  }
}

export function comparable(value1, value2) {
  const unitRegex = /[a-zA-Z%]+$/; // Regex to extract unit

  const unit1 = String(value1).match(unitRegex)?.[0] || '';
  const unit2 = String(value2).match(unitRegex)?.[0] || '';

  return unit1 === unit2;
}

export function unit(value) {
  return splitUnit(value)[1];
}

export function splitUnit(value) {
  const unitRegex = /[a-zA-Z%]+$/; // Regex to extract unit

  const text = String(value);
  const unit = text.match(unitRegex)?.[0] || '';
  value = +text.substring(0, text.length - unit.length);

  return [value, unit];
}

export function setUnit(value, unit) {
  if (value === null) {
    value = 0;
  }

  const [unitlessValue] = splitUnit(value);

  return `${unitlessValue}${unit}`;
}

export function typeOf(value) {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'list';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'bool';
  if (typeof value === 'string') {
    return isCssUnitValue(value) ? 'number' : 'string';
  }
  if (typeof value === 'object') return 'map';

  return 'unknown';
}

// Helper function to check if a string contains a CSS unit
export function isCssUnitValue(value) {
  return /^-?\d+(\.\d+)?(px|em|rem|vh|vw|vmin|vmax|%|cm|mm|in|pt|pc|ex|ch)?$/.test(value);
}

export function compareValues(value1, op, value2) {
  // Convert values to numbers
  let [num1, unit1] = splitUnit(value1);
  let [num2, unit2] = splitUnit(value2);

  // If both have the same unit, compare directly
  if (unit1 !== unit2 && unit1 && unit2) {
    throw new Error(`Cannot compare different units: ${unit1} vs ${unit2}. comparison: ${value1} ${op} ${value2}`);
  }

  switch (op) {
    case '==':
      return num1 == num2;
    case '===':
      return num1 === num2;
    case '!=':
      return num1 != num2;
    case '!==':
      return num1 !== num2;
    case '<':
      return num1 < num2;
    case '<=':
      return num1 <= num2;
    case '>':
      return num1 > num2;
    case '>=':
      return num1 >= num2;
    default:
      throw new Error(`Invalid comparison operator: ${op}`);
  }
}

// // Replace `$search` with `$replace` in `$string`
// // Used on our SVG icon backgrounds for custom forms.
// //
// // @author Hugo Giraudel
// // @param {String} $string - Initial string
// // @param {String} $search - Substring to replace
// // @param {String} $replace ('') - New value
// // @return {String} - Updated string
// @function str-replace($string, $search, $replace: '') {
//     $index: str-index($string, $search);

//     @if $index {
//         @return str-slice($string, 1, $index - 1) + $replace + str-replace(str-slice($string, $index + str-length($search)), $search, $replace);
//     }

//     @return $string;
// }

// // See https://codepen.io/kevinweber/pen/dXWoRw
// //
// // Requires the use of quotes around data URIs.

// @function escape-svg($string) {
//     @if str-index($string, 'data:image/svg+xml') {
//         @each $char, $encoded in $escaped-characters {
//             // Do not escape the url brackets
//             @if str-index($string, 'url(') == 1 {
//                 $string: url('#{str-replace(str-slice($string, 6, -3), $char, $encoded)}');
//             } @else {
//                 $string: str-replace($string, $char, $encoded);
//             }
//         }
//     }

//     @return $string;
// }

// Color contrast
/**
 * Determines whether a given color should use a light or dark contrasting text color
 * based on the YIQ color model for brightness contrast.
 *
 * @function colorYiq
 * @param {Object} $ - Configuration object that should contain:
 *   @param {string} $.yiqTextDark - The dark text color (used for light backgrounds).
 *   @param {string} $.yiqTextLight - The light text color (used for dark backgrounds).
 *   @param {number} $.yiqContrastedThreshold - The brightness threshold to determine contrast.
 * @param {string} color - The base color in any CSS-compatible format (e.g., hex, rgb, hsl).
 * @param {string} [dark] - Optional override for the dark text color.
 * @param {string} [light] - Optional override for the light text color.
 *
 * @returns {string} - Returns the text color (`dark` or `light`) that best contrasts with the input `color`.
 *
 * @example
 * const theme = { yiqTextDark: '#000', yiqTextLight: '#fff', yiqContrastedThreshold: 150 };
 * colorYiq(theme, '#f0f0f0'); // → '#000' (dark text)
 */
export function colorYiq($, color, dark = undefined, light = undefined) {
  dark = $.yiqTextDark;
  light = $.yiqTextLight;
  const rgb = parseToRgb(color);
  const r = rgb.red;
  const g = rgb.green;
  const b = rgb.blue;

  const yiq = (r * 299 + g * 587 + b * 114) * 0.001;

  if (yiq >= $.yiqContrastedThreshold) {
    return dark;
  } else {
    return light;
  }
}

/**
 * Converts a color to an RGBA string with a specified alpha transparency value.
 *
 * @function colorWithAlpha
 * @param {string} color - The base color in any CSS-compatible format (e.g., hex, rgb, hsl).
 * @param {number} alpha - The alpha (opacity) value between 0 (fully transparent) and 1 (fully opaque).
 *
 * @returns {string} - Returns the color as an RGBA string (e.g., `rgba(255, 0, 0, 0.5)`).
 *
 * @example
 * colorWithAlpha('#ff0000', 0.5); // → 'rgba(255, 0, 0, 0.5)'
 */
export function colorWithAlpha(color, alpha) {
  const { red, green, blue } = parseToRgb(color);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

// // Retrieve color Sass maps
// @function color($key: 'blue') {
//     @return map-get($colors, $key);
// }

function themeColor($, key = 'primary') {
  return $.themeColors[key];
}

// @function gray($key: '100') {
//     @return map-get($grays, $key);
// }

// Request a theme color level
export function themeColorLevel($, colorName = 'primary', level = 0) {
  const color = themeColor($, colorName);
  const colorBase = level > 0 ? $.black : $.white;
  level = Math.abs(level);

  return mix(math(`${level} * ${percentageToDecimal($.themeColorInterval)}`), colorBase, color);
}

export function abs(value) {
  const [num, unit] = splitUnit(value);
  return `${Math.abs(num)}${unit}`;
}

// Return valid calc
export function add(value1, value2, returnCalc = true) {
  if (value1 === null) {
    return value2;
  }

  if (value2 === null) {
    return value1;
  }

  if (typeOf(value1) === 'number' && typeOf(value2) == 'number' && comparable(value1, value2)) {
    return value1 + value2;
  }
  const expr = `${value1} + ${value2}`;

  return returnCalc === true ? `calc(${expr})` : math(expr);
}

export function dataUrl(contentType, content) {
  return `url(data:${contentType},${content})`;
}

export function escapeSVG(svgString) {
  return svgString
    .replace(/"/g, "'") // Replace double quotes with single quotes
    .replace(/#/g, '%23') // Escape hash symbol
    .replace(/\s+/g, ' ') // Collapse whitespace
    .trim(); // Remove leading/trailing spaces
}

export function subtract(value1, value2, returnCalc = true) {
  if (value1 === null && value2 === null) {
    return null;
  }

  if (value1 === null) {
    return '-$value2';
  }

  if (value2 === null) {
    return value1;
  }

  if (typeOf(value1) === 'number' && typeOf(value2) === 'number' && comparable(value1, value2)) {
    return value1 - value2;
  }

  if (typeOf(value2) !== 'number') {
    value2 = `(${value2})`;
  }

  const expr = `${value1} - ${value2}`;

  return returnCalc === true ? `calc(${expr})` : expr;
}

export function percentage(value) {
  if (typeOf(value) === 'number') {
    const [num, unit] = splitUnit(value);
    if (unit === '%') {
      return value;
    } else if (unit === '') {
      return `${value * 100}%`;
    }
  }

  throw new Error(`Error: $number: ${value} is not a unitless number.`);
}

export function percentageToDecimal(value) {
  if (typeOf(value) === 'number') {
    const [num, unit] = splitUnit(value);
    if (unit === '%') {
      return num / 100;
    } else if (unit === '') {
      return value;
    }
  }

  throw new Error(`Error: $number: ${value} is not a unitless number.`);
}

export function divide(dividend, divisor, precision = 10) {
  const [dividendValue, dividendUnit] = splitUnit(dividend);
  const [divisorValue, divisorUnit] = splitUnit(divisor);
  const quotient = Math.round((dividendValue / divisorValue) * precision) / precision;

  if (dividendUnit !== divisorUnit) {
    return setUnit(quotient, dividendUnit);
  } else {
    return setUnit(quotient, '');
  }
}
